import formErrors from '../locales/es/formErrors';

/**
 * Keywords whose errors only say "something inside me failed". ajv always
 * reports the inner errors as well, so when one of those is present the
 * wrapper adds nothing but noise (e.g. `should match "then" schema`).
 */
const WRAPPER_KEYWORDS = new Set(['if', 'anyOf', 'oneOf']);

function isNoise(error) {
    return WRAPPER_KEYWORDS.has(error?.name) || error?.name === 'const';
}

/** Returns a predicate telling whether an error was produced inside the given wrapper error. */
function isInside(wrapper) {
    const wrapperPath = wrapper.schemaPath || '';
    if (wrapper.name === 'if') {
        // "#/.../if" -> its then/else branches are siblings of the "if" keyword
        const base = wrapperPath.replace(/\/if$/, '');
        return (e) => (e.schemaPath || '').startsWith(`${base}/then/`)
            || (e.schemaPath || '').startsWith(`${base}/else/`);
    }
    return (e) => (e.schemaPath || '').startsWith(`${wrapperPath}/`);
}

/**
 * Removes errors that carry no signal of their own:
 *  - `const` errors: they come from oneOf branches that do not apply (branch selection)
 *    or from enum-like oneOf lists, which already report a oneOf error on the field.
 *  - if / anyOf / oneOf wrapper errors, but only when a concrete error was reported inside them.
 *  - oneOf errors where more than one branch matched (`params.passingSchemas`) are always kept.
 *
 * Never turns an invalid form into a valid one: if everything would be removed,
 * the original list is returned.
 */
export function denoiseErrors(errors) {
    if (!Array.isArray(errors) || !errors.length) return errors;

    const concrete = errors.filter(e => !isNoise(e));

    const filtered = errors.filter(error => {
        if (!isNoise(error)) return true;
        if (error.name === 'const') return false;
        if (error.name === 'oneOf' && error.params?.passingSchemas) return true;
        return !concrete.some(isInside(error));
    });

    return filtered.length ? filtered : errors;
}

export function collectSchemaChainFor(path, schema) {
    const schemas = [schema];
    let currentSchema = schema;
    (path || '').split('.').forEach(component => {
        if (!currentSchema) return;
        switch (currentSchema.type) {
            case 'object': currentSchema = currentSchema.properties?.[component]; break;
            case 'array': currentSchema = currentSchema.items; break;
            default: currentSchema = null; break;
        }
        if (currentSchema) schemas.push(currentSchema);
    });

    return schemas;
}

function translateOneError(error, schema) {
    const translate = formErrors[error?.name];
    if (!translate) return error;

    const schemaChain = collectSchemaChainFor((error.property || '').substring(1), schema);
    const title = schemaChain.map(sc => (sc?.title || "").trim()).filter(x => !!x).pop() || "";

    return {
        ...error,
        title,
        message: translate(error)
    };
}

/** RJSF `transformErrors`: drops noise errors and translates the rest to Spanish. */
export default function transformErrors(errors, schema) {
    return errors && denoiseErrors(errors).map(error => translateOneError(error, schema));
}
